import { useEffect, useId } from 'react';
import ColumnsIcon from 'assets/icons/columns';
import { Stack, Typography, Select, MenuItem, Checkbox, ListItemText, OutlinedInput } from '@mui/material';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

const MenuProps = {
	PaperProps: {
		style: {
			width: 200,
			marginTop: '4px',
			borderRadius: '4px',
			border: '1px solid #EdEdEd',
			maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
			boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.15)',
		},
	},
	disableAutoFocusItem: true,
	anchorOrigin: {
		vertical: 'bottom',
		horizontal: 'left',
	},
	transformOrigin: {
		vertical: 'top',
		horizontal: 'left',
	},
};

export default function SelectVisibleColumn({ getToggleHideAllColumnsProps, allColumns, visibleColumns = [] }) {
	const selectId = useId();

	useEffect(() => {
		const styleId = 'select-column-styles';

		// Check if styles already exist
		if (document.getElementById(styleId)) {
			return;
		}

		const style = document.createElement('style');
		style.id = styleId;
		style.textContent = `
      /* Fix for Ant Design legend interference */
      .ant-form legend {
        height: 0 !important;
        margin: 0 !important;
        padding: 0 !important;
        visibility: hidden !important;
        font-size: 0 !important;
        line-height: 0 !important;
      }
    `;

		document.head.appendChild(style);

		// Cleanup function
		return () => {
			const existingStyle = document.getElementById(styleId);
			if (existingStyle && existingStyle.parentNode) {
				existingStyle.parentNode.removeChild(existingStyle);
			}
		};
	}, []);

	const changeHandler = (e, getToggleHiddenProps) => {
		const { onChange, checked } = getToggleHiddenProps();
		e.target.checked = !checked;
		onChange(e);
	};

	return (
		<Select
			multiple
			displayEmpty
			id={selectId}
			value={visibleColumns}
			input={
				<OutlinedInput
					id="Search"
					autoComplete="off"
					sx={
						{
							// '& .MuiOutlinedInput-notchedOutline': {
							// 	borderRadius: '4px',
							// 	border: '1px solid #EdEdEd',
							// },
						}
					}
				/>
			}
			sx={{
				width: 200,
				height: '100%',
				borderRadius: '4px',
				backgroundColor: 'common.white',
				border: '1px solid #EdEdEd',

				'&.Mui-focused': {
					boxShadow: 'none',
				},

				'& .MuiOutlinedInput-input': { padding: '5px 12px' },

				'& .MuiOutlinedInput-notchedOutline': {
					border: 'none',
				},

				'&:hover .MuiOutlinedInput-notchedOutline': {
					border: 'none',
				},

				'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
					border: 'none',
				},
			}}
			renderValue={() => {
				if (visibleColumns?.length === allColumns?.length) {
					return (
						<Stack direction="row" gap={1} sx={{ alignItems: 'center' }}>
							{/* <LynIcon folder="icons" fileName={'rows'} format="svg" sx={{ marginRight: '4px' }} /> */}

							<ColumnsIcon color="#000D1A" width="18px" height="18px" />
							<Typography style={{ mt: '12px' }}>Columns</Typography>
						</Stack>
					);
				} else {
					return (
						<Typography>
							{visibleColumns?.length} of {allColumns?.length} Columns Visible
						</Typography>
					);
				}
			}}
			MenuProps={MenuProps}
			inputProps={{ 'aria-label': 'Column visibility selector' }}
		>
			<IndeterminateCheckbox {...getToggleHideAllColumnsProps()} />
			{allColumns.map(
				(column, index) =>
					typeof column.Header === 'string' && (
						<MenuItem
							key={column.id || index}
							value={column.Header}
							onClick={(e) => changeHandler(e, column.getToggleHiddenProps)}
							sx={{ paddingLeft: '10px', '& .MuiButtonBase-root': { padding: '1px 4px 1px 0px' } }}
						>
							<Checkbox
								size="small"
								sx={{
									color: '#858585',
									'&.Mui-checked': {
										// color: '#214689',
										color: '#1976D2',
									},
									'&.MuiCheckbox-indeterminate': {
										// color: '#214689',
										color: '#1976D2',
									},
								}}
								checked={column.getToggleHiddenProps().checked}
							/>
							<ListItemText primary={column.Header} />
						</MenuItem>
					)
			)}
		</Select>
	);
}

const IndeterminateCheckbox = ({ indeterminate, onChange, checked, style }) => {
	const changeHandler = (e, onChange, checked) => {
		e.target['checked'] = !checked;
		onChange(e);
	};

	return (
		<MenuItem
			{...style}
			onClick={(e) => changeHandler(e, onChange, checked)}
			sx={{ paddingLeft: '10px', '& .MuiButtonBase-root': { padding: '1px 4px 1px 0px' } }}
		>
			<Checkbox
				size="small"
				id="select-all"
				sx={{
					pl: 0,
					color: '#858585',

					'&.Mui-checked': {
						// color: '#214689',
						color: '#1976D2',
					},
					'&.MuiCheckbox-indeterminate': {
						// color: '#214689',
						color: '#1976D2',
					},
				}}
				onChange={onChange}
				checked={checked}
				indeterminate={indeterminate > 0 ? true : false}
			/>
			<ListItemText primary="Select All" />
		</MenuItem>
	);
};

export function SelectVisibleColumnV8({ table }) {
	let random = Math.floor(Math.random() * 100000).toString();

	return (
		<Select
			multiple
			value={[]}
			displayEmpty
			autoComplete="off"
			id={'Select' + random}
			input={
				<OutlinedInput
					autoComplete="off"
					id="Search"
					sx={{
						'& .MuiOutlinedInput-notchedOutline': {
							borderWidth: 0,
							outline: 'none',
							borderRadius: '0',
						},
					}}
				/>
			}
			sx={{
				width: 200,
				height: '36px',
				borderRadius: '10px',
				backgroundColor: 'common.white',
				border: '1px solid #E2E8F0',

				'&.Mui-focused': {
					boxShadow: 'none',
				},

				'& .MuiOutlinedInput-root': {
					height: '36px',
					borderRadius: '10px',
				},

				'& .MuiOutlinedInput-input': {
					padding: '5px 12px',
					border: 'none !important',
				},

				'& .MuiOutlinedInput-notchedOutline': {
					borderWidth: 0,
					display: 'none',
					outline: 'none',
					borderRadius: '0',
				},

				'&:hover .MuiOutlinedInput-notchedOutline': {
					border: 'none',
				},

				'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
					border: 'none',
				},
			}}
			renderValue={() => {
				if (table.getVisibleLeafColumns()?.length === table.getAllLeafColumns()?.length) {
					return (
						<Stack direction="row" gap={1} sx={{ alignItems: 'center' }}>
							<ColumnsIcon color="#000D1A" width="18px" height="18px" />
							{/* <LynIcon folder="icons" fileName={'rows'} format="svg" sx={{ marginRight: '4px' }} /> */}
							<Typography style={{ mt: '12px' }}>Columns</Typography>
						</Stack>
					);
				} else {
					return (
						<Typography>
							{table.getVisibleLeafColumns()?.length} of {table.getAllLeafColumns()?.length} Columns Visible
						</Typography>
					);
				}
			}}
			MenuProps={MenuProps}
			inputProps={{ 'aria-label': 'Without label' }}
		>
			<IndeterminateCheckboxV8 table={table} />
			{table.getAllLeafColumns().map(
				(column, index) =>
					typeof column.columnDef.header === 'string' && (
						<MenuItem
							key={column.id}
							value={column.Header}
							onClick={column.getToggleVisibilityHandler()}
							sx={{ paddingLeft: '10px', '& .MuiButtonBase-root': { padding: '1px 4px 1px 0px' } }}
						>
							<Checkbox
								size="small"
								id={'Select' + index + ''}
								sx={{
									pl: 0,
									color: '#858585',

									'&.Mui-checked': {
										// color: '#214689',
										color: '#1976D2',
									},
								}}
								checked={column.getIsVisible()}
							/>
							<ListItemText primary={column.columnDef.header} />
						</MenuItem>
					)
			)}
		</Select>
	);
}

const IndeterminateCheckboxV8 = ({ table }) => {
	return (
		<MenuItem
			onClick={table.getToggleAllColumnsVisibilityHandler()}
			sx={{ paddingLeft: '10px', '& .MuiButtonBase-root': { padding: '1px 4px 1px 0px' } }}
		>
			<Checkbox
				size="small"
				id="select-all"
				checked={table.getIsAllColumnsVisible()}
				sx={{
					pl: 0,
					color: '#858585',

					'&.Mui-checked': {
						// color: '#214689',
						color: '#1976D2',
					},
				}}
			/>

			<ListItemText primary="Select All" />
		</MenuItem>
	);
};
